import Button from "@/common/components/Button";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import { ImageWithPlaceholder } from "@/common/components/ImageWithPlaceholder";
import { ShowHeader } from "@/common/components/ShowHeader";
import Table from "@/common/components/Table";
import TextField from "@/common/components/TextField";
import useDebounce from "@/common/hooks/useDebounce";
import { useFilterParams } from "@/common/hooks/useFilterParams";
import { companyScrapRulesDefaultColumns } from "@/features/privateCompanies/constants/companyScrapRulesDefaultColumns.constant";
import { useCompany } from "@/features/privateCompanies/hooks/useCompany";
import { useScrapRules } from "@/features/privateCompanies/hooks/useScrapRules";
import { BriefcaseBusinessIcon, ChevronsRight, SearchIcon } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { ScrapRulesTransformer } from "../../../services/maintenanceScrapRules/scrapRulesTransformer";

const ScrapRulesList = () => {
  const { companyId } = useParams();

  const navigate = useNavigate();

  const { filterParams, setFilterParams } = useFilterParams();

  const [search, setSearch] = useDebounce(filterParams.search);

  const { data: companyData } = useCompany(companyId || '');

  const {
    meta,
    data: scrapRules,
    isPending: scrapRulesIsPending,
    error: scrapRulesError = {} as any,
    refetch: refetchScrapRules,
  } = useScrapRules({
    filters: {
      search: search,
      page: Number(filterParams.page) || 1,
      companyId
    },
    mapper: ScrapRulesTransformer.tableTransformer(),
  });

  return (
    <ErrorMessageWrapper
      className="h-[calc(100vh-192px)]"
      statusCode={scrapRulesError?.status}
      refetchFunction={refetchScrapRules}
    >
      <div className="flex flex-col">
        <ShowHeader
          goBack
          goBackProps={{
            fallback: `/companies/${companyId}/maintenance`,
            searchLastRouteFromFallback: true
          }}
          titleClassName="text-nowrap overflow-hidden text-ellipsis font-normal text-base"
          title={
            <div className="flex gap-2 items-center">
              <div className="flex gap-2 items-center">
                <ImageWithPlaceholder
                  url={companyData?.general_information?.company_logo?.url}
                  alt={companyData?.general_information?.fantasy_name}
                  className="size-8"
                  iconClassName="size-6"
                  placeholderIcon={BriefcaseBusinessIcon}
                />
                <p className="text-white-400 font-normal">{companyData?.general_information?.fantasy_name}</p>
              </div>
              <ChevronsRight color='#ffffffb3' />
              <h2 className="text-xl font-bold">Regras para desfazimento</h2>
            </div>
          }
          buttons={(
            <Button
              type="submit"
              name="create"
              operationKlassToCheckPermission="Admin::Maintenance::ScrapRules::Create"
              onClick={() => navigate('create')}
            >
              Criar regra
            </Button>
          )}
        />
        <div className="flex flex-col gap-6">
          <TextField
            LeftIcon={<SearchIcon />}
            placeholder="Pesquisar"
            className="rounded-sm shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.5)]"
            onChange={(e) => {
              setSearch((e.target as HTMLInputElement).value);
              setFilterParams((prev) => ({ ...prev, search: (e.target as HTMLInputElement).value, page: 1 }));
            }}
            value={filterParams.search}
          />

          <Table
            className="max-h-[calc(100vh-324px)]"
            columns={companyScrapRulesDefaultColumns}
            isLoadingData={scrapRulesIsPending}
            page={filterParams.page || 1}
            rows={scrapRules}
            totalPages={meta.total_pages}
            totalCount={meta.total}
            hasPagination
            setPage={(page) => setFilterParams((prev) => ({ ...prev, page }))}
          />
        </div>
      </div>
    </ErrorMessageWrapper>
  );
}

export default ScrapRulesList;
